"use client"

import type React from "react"
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { FiFilter, FiX, FiRefreshCw, FiSearch as FiSearchIcon, FiBookOpen, FiCalendar, FiFileText, FiExternalLink } from "react-icons/fi"
import { FaPlus, FaEdit, FaCertificate, FaCheck, FaTimes as FaTimesIcon, FaSort, FaSortUp, FaSortDown, FaRupeeSign, FaSearch, FaShieldAlt, FaCheckCircle, FaSpinner, FaFileExcel } from "react-icons/fa"
import { Pagination as PaginationComponent } from "../../../utils/pagination.utils"
import { TrainingCertification, Pagination } from "../types"
import type { SelectOption } from "../../../utils/select-with-search"
import { SearchableSelect } from "../../../utils/select-with-search"
import { useAuth } from "../../../provider/auth.provider"
import { ExcelDownload } from "../../../utils/excel.utils"
import toast from "react-hot-toast"
import api from "../../../libs/axios"

export interface CertificationFilters {
  [key: string]: string | undefined
  search?: string
  deduction_status?: string
  is_verified?: string
  training_request_id?: string
  training_request_label?: string
  certificate_number?: string
  training_type_id?: string
  training_type_label?: string
  training_institute_id?: string
  training_institute_label?: string
  training_date_from?: string
  training_date_to?: string
  expiry_date_from?: string
  expiry_date_to?: string
  created_from?: string
  created_to?: string
  training_amount_min?: string
  training_amount_max?: string
  deduction_amount_min?: string
  deduction_amount_max?: string
  card_front_url?: string
  card_back_url?: string
}

type TrainingCertificationExportRow = Record<string, string | number | boolean | null>

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: CertificationFilters
  onFiltersChange: (filters: CertificationFilters) => void
  onApply: () => void
  onReset: () => void
  trainingTypeOptions: SelectOption[]
  trainingInstituteOptions: SelectOption[]
  trainingRequestOptions: SelectOption[]
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  trainingTypeOptions,
  trainingInstituteOptions,
  trainingRequestOptions,
}) => {
  const [activeTab, setActiveTab] = useState("basic")
  const [selectedTrainingType, setSelectedTrainingType] = useState<SelectOption | null>(null)
  const [selectedInstitute, setSelectedInstitute] = useState<SelectOption | null>(null)
  const [selectedTrainingRequest, setSelectedTrainingRequest] = useState<SelectOption | null>(null)

  useEffect(() => {
    if (isOpen) {
      setActiveTab("basic")
    }
  }, [isOpen])

  useEffect(() => {
    if (filters.training_type_id && filters.training_type_label) {
      setSelectedTrainingType({
        id: Number(filters.training_type_id),
        value: filters.training_type_id,
        label: filters.training_type_label,
      })
    } else {
      setSelectedTrainingType(null)
    }
  }, [filters.training_type_id, filters.training_type_label])

  useEffect(() => {
    if (filters.training_institute_id && filters.training_institute_label) {
      setSelectedInstitute({
        id: Number(filters.training_institute_id),
        value: filters.training_institute_id,
        label: filters.training_institute_label,
      })
    } else {
      setSelectedInstitute(null)
    }
  }, [filters.training_institute_id, filters.training_institute_label])

  useEffect(() => {
    if (filters.training_request_id && filters.training_request_label) {
      setSelectedTrainingRequest({
        id: Number(filters.training_request_id),
        value: filters.training_request_id,
        label: filters.training_request_label,
      })
    } else {
      setSelectedTrainingRequest(null)
    }
  }, [filters.training_request_id, filters.training_request_label])

  const tabs = useMemo(
    () => [
      { id: "basic", label: "Basic Info", icon: FiSearchIcon },
      { id: "training", label: "Training Details", icon: FiBookOpen },
      { id: "dates", label: "Dates", icon: FiCalendar },
      { id: "amounts", label: "Amounts", icon: FaRupeeSign },
      { id: "documents", label: "Documents", icon: FiFileText },
    ],
    [],
  )

  if (!isOpen) return null

  const handleInputChange = (field: keyof CertificationFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    })
  }

  const handleTrainingTypeSelect = (option: SelectOption | null) => {
    setSelectedTrainingType(option)
    onFiltersChange({
      ...filters,
      training_type_id: option ? String(option.value) : undefined,
      training_type_label: option ? option.label : undefined,
    })
  }

  const handleInstituteSelect = (option: SelectOption | null) => {
    setSelectedInstitute(option)
    onFiltersChange({
      ...filters,
      training_institute_id: option ? String(option.value) : undefined,
      training_institute_label: option ? option.label : undefined,
    })
  }

  const handleTrainingRequestSelect = (option: SelectOption | null) => {
    setSelectedTrainingRequest(option)
    onFiltersChange({
      ...filters,
      training_request_id: option ? String(option.value) : undefined,
      training_request_label: option ? option.label : undefined,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id ? "border-blue-500 text-blue-600 bg-white" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Search by certificate no. or candidate"
                  value={filters.search || ""}
                  onChange={(e) => handleInputChange("search", e.target.value)}
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="e.g., ESTAC-2026-0012"
                  value={filters.certificate_number || ""}
                  onChange={(e) => handleInputChange("certificate_number", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Deduction Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.deduction_status || ""}
                  onChange={(e) => handleInputChange("deduction_status", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="deducted">Deducted</option>
                  <option value="waived">Waived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Verification Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.is_verified || ""}
                  onChange={(e) => handleInputChange("is_verified", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Verified</option>
                  <option value="false">Unverified</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Request</label>
                <SearchableSelect
                  value={selectedTrainingRequest}
                  onChange={handleTrainingRequestSelect}
                  options={trainingRequestOptions}
                  placeholder="Search requests"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
            </div>
          )}

          {activeTab === "training" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Institute</label>
                <SearchableSelect
                  value={selectedInstitute}
                  onChange={handleInstituteSelect}
                  options={trainingInstituteOptions}
                  placeholder="Search institutes"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Type</label>
                <SearchableSelect
                  value={selectedTrainingType}
                  onChange={handleTrainingTypeSelect}
                  options={trainingTypeOptions}
                  placeholder="Search training types"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
            </div>
          )}

          {activeTab === "dates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.training_date_from || ""}
                  onChange={(e) => handleInputChange("training_date_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.training_date_to || ""}
                  onChange={(e) => handleInputChange("training_date_to", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.expiry_date_from || ""}
                  onChange={(e) => handleInputChange("expiry_date_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  value={filters.expiry_date_to || ""}
                  onChange={(e) => handleInputChange("expiry_date_to", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  value={filters.created_from || ""}
                  onChange={(e) => handleInputChange("created_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  value={filters.created_to || ""}
                  onChange={(e) => handleInputChange("created_to", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "amounts" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {["training_amount_min", "training_amount_max", "deduction_amount_min", "deduction_amount_max"].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.includes("training") ? `Training Amount ${field.endsWith("min") ? "Min" : "Max"}` : `Deduction Amount ${field.endsWith("min") ? "Min" : "Max"}`}
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={filters[field as keyof CertificationFilters] || ""}
                    onChange={(e) => handleInputChange(field as keyof CertificationFilters, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}

          {activeTab === "documents" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Card Front URL</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="https://"
                  value={filters.card_front_url || ""}
                  onChange={(e) => handleInputChange("card_front_url", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Card Back URL</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  placeholder="https://"
                  value={filters.card_back_url || ""}
                  onChange={(e) => handleInputChange("card_back_url", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearchIcon className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CertificationsListProps {
  certifications: TrainingCertification[]
  pagination: Pagination
  onDeductionFilter: (status: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdd: () => void
  onEdit: (cert: TrainingCertification) => void
  onVerify?: (id: number) => void
  loading?: boolean
  sortConfig?: { field: string; direction: "ASC" | "DESC" }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  onAdvancedFilter?: (filters: CertificationFilters) => void
  advancedFilters?: CertificationFilters
  trainingTypeOptions?: SelectOption[]
  trainingInstituteOptions?: SelectOption[]
  trainingRequestOptions?: SelectOption[]
}

export const CertificationsList: React.FC<CertificationsListProps> = ({
  certifications,
  pagination,
  onDeductionFilter,
  onPageChange,
  onLimitChange,
  onAdd,
  onEdit,
  onVerify,
  loading = false,
  sortConfig,
  onSort,
  onAdvancedFilter,
  advancedFilters = {},
  trainingTypeOptions = [],
  trainingInstituteOptions = [],
  trainingRequestOptions = [],
}) => {
  const { hasPermission } = useAuth()
  const canCreateCertification = hasPermission("TrainingCertification", "create")
  const canEditCertification = hasPermission("TrainingCertification", "edit")
  const canVerifyCertification = hasPermission("TrainingCertification", "verify")

  const [deductionFilter, setDeductionFilter] = useState("")
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [filters, setFilters] = useState<CertificationFilters>(advancedFilters)
  const [searchTerm, setSearchTerm] = useState(advancedFilters.search || "")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<TrainingCertificationExportRow[]>([])
  const [projectsPopup, setProjectsPopup] = useState<{ certId: number; projects: string[] } | null>(null)

  useEffect(() => {
    setFilters(advancedFilters)
    setSearchTerm(advancedFilters.search || "")
  }, [advancedFilters])

  const getActiveFiltersCount = () =>
    Object.entries(advancedFilters)
      .filter(([key, value]) => !key.endsWith("_label") && value !== undefined && value !== "")
      .length

  const handleFiltersChange = (updatedFilters: CertificationFilters) => {
    setFilters(updatedFilters)
  }

  const handleResetFilters = () => {
    setFilters({})
    setSearchTerm("")
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
    setIsFilterModalOpen(false)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    const normalizedValue = value || undefined
    const updatedFilters = {
      ...advancedFilters,
      search: normalizedValue,
    }
    setFilters((prev) => ({
      ...prev,
      search: normalizedValue,
    }))
    onAdvancedFilter?.(updatedFilters)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
  }

  const formatAmount = (amount?: number) => {
    if (!amount && amount !== 0) return "-"
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount)
  }

  const formatDateTimeValue = (value?: string | null) => {
    if (!value) return "-"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "-"
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getDeductionBadge = (status: string, amount?: number | string | null) => {
    const styles: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      deducted: "bg-green-100 text-green-800",
      payment_received: "bg-emerald-100 text-emerald-800",
      waived: "bg-blue-100 text-blue-800",
    }
    const labels: Record<string, string> = {
      pending: "Deduction Pending",
      deducted: "Deduction Booked",
      payment_received: "Payment Received",
      waived: "Deduction to be Booked",
    }
    return (
      <div className="flex flex-col items-start gap-1">
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
          {labels[status] || status}
        </span>
        {amount != null && amount !== "" && (
          <span className="text-xs font-medium text-gray-700">₹{Number(amount).toLocaleString("en-IN")}</span>
        )}
      </div>
    )
  }

  const getVerificationBadge = (cert: TrainingCertification) => {
    if (cert.is_verified) {
      return (
        <div className="flex flex-col gap-1">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaCheckCircle className="w-3 h-3" />
            Verified
          </span>
          {cert.verifier && (
            <span className="text-xs text-gray-500">
              by {cert.verifier.first_name} {cert.verifier.last_name}
            </span>
          )}
        </div>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        <FaTimesIcon className="w-3 h-3" />
        Not Verified
      </span>
    )
  }

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false
    const expiry = new Date(expiryDate)
    const now = new Date()
    const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return diffDays <= 30 && diffDays > 0
  }

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false
    return new Date(expiryDate) < new Date()
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction = sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig?.field !== field) return <FaSort className="w-3 h-3 text-gray-400" />
    if (sortConfig.direction === "ASC") return <FaSortUp className="w-3 h-3 text-blue-600" />
    return <FaSortDown className="w-3 h-3 text-blue-600" />
  }

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number> = { page, limit }

    if (deductionFilter) {
      params.deduction_status = deductionFilter
    }

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (key.endsWith("_label")) return
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value
      }
    })

    const trimmedSearch = searchTerm.trim()
    if (trimmedSearch && !params.search) {
      params.search = trimmedSearch
    }

    if (sortConfig?.field) {
      params.sort_by = sortConfig.field
      params.sort_order = sortConfig.direction
    }

    return params
  }

  const getLinkedUsers = (cert: TrainingCertification) => {
    const linkedUsers = (cert.certifications || []).map((link) => link.user).filter(Boolean)
    if (linkedUsers.length > 0) {
      return linkedUsers
    }
    return cert.training_request?.linked_user ? [cert.training_request.linked_user] : []
  }

  const getDepartmentNames = (cert: TrainingCertification) => {
    return Array.from(new Set(getLinkedUsers(cert).map((user) => user?.department?.name).filter(Boolean)))
  }

  const getProjectGroupNames = (cert: TrainingCertification) => {
    const projectNames = getLinkedUsers(cert).flatMap((user) =>
      (user?.user_projects || []).map((userProject) => userProject.project?.project_name).filter((name): name is string => Boolean(name))
    )
    return Array.from(new Set(projectNames))
  }

  const getReportingPersonNames = (cert: TrainingCertification) => {
    const reportingNames = getLinkedUsers(cert)
      .map((user) => {
        const firstName = user?.project_manager?.first_name || ""
        const lastName = user?.project_manager?.last_name || ""
        const fullName = `${firstName} ${lastName}`.trim()
        return fullName || undefined
      })
      .filter(Boolean)

    return Array.from(new Set(reportingNames))
  }

  const mapCertificationForExport = (cert: TrainingCertification): TrainingCertificationExportRow => {
    const departmentNames = getDepartmentNames(cert)
    const projectGroupNames = getProjectGroupNames(cert)
    const reportingPersonNames = getReportingPersonNames(cert)

    return {
      "Certificate Number": cert.certificate_number,
      "Training Request": cert.training_request?.request_id || "-",
      "Candidate Name": cert.training_request?.candidate_name || "-",
      "Candidate Phone": cert.training_request?.candidate_phone || "-",
      "Department": departmentNames.join(", ") || "-",
      "Project Group": projectGroupNames.join(", ") || "-",
      "Reporting Person": reportingPersonNames.join(", ") || "-",
      "Candidate Association": cert.training_request?.association_type || "-",
      "Training Type": cert.training_type?.training_name || "-",
      "Training Type Description": cert.training_type?.training_description || "-",
      "Training Institute": cert.training_institute?.institute_name || "-",
      "Institute Short Name": cert.training_institute?.institute_short_name || "-",
      "Institute Contact Person": cert.training_institute?.contact_person || "-",
      "Institute Contact Phone": cert.training_institute?.contact_phone || "-",
      "Institute Contact Email": cert.training_institute?.contact_email || "-",
      "Training Date": formatDate(cert.training_date),
      "Expiry Date": formatDate(cert.expiry_date),
      "Training Amount": cert.training_amount ?? "-",
      "Card Front URL": cert.card_front_url || "-",
      "Card Back URL": cert.card_back_url || "-",
      "Deduction Status": cert.deduction_status,
      "Deduction Amount": cert.deduction_amount ?? "-",
      "Deduction Booked Date": formatDate(cert.deduction_date),
      "Deduction Remarks": cert.deduction_remarks || "-",
      "Deducted At": formatDateTimeValue(cert.deducted_at),
      "Created By": cert.creator?.first_name && cert.creator?.last_name ? `${cert.creator.first_name} ${cert.creator.last_name}` : "-",
      "Creator Email": cert.creator?.email || "-",
      "Created At": formatDateTimeValue(cert.created_at),
      "Updated At": formatDateTimeValue(cert.updated_at),
    }
  }

  const fetchExportData = async () => {
    if (exporting) return

    try {
      setExporting(true)
      setExportData([])
      const pageSize = 100
      let page = 1
      let hasMore = true
      const allCertifications: TrainingCertification[] = []

      while (hasMore) {
        const params = buildExportParams(page, pageSize)
        const response = await api.get("/training/certifications/list", { params })

        if (!response.data?.success) {
          throw new Error(response.data?.message || "Failed to fetch certifications")
        }

        const data = response.data.data || {}
        const fetched: TrainingCertification[] = data.training_certifications || data.certifications || []
        const paginationInfo = data.pagination

        allCertifications.push(...fetched)

        const nextPageAvailable = paginationInfo?.has_next_page ??
          (paginationInfo?.total_pages ? page < paginationInfo.total_pages : fetched.length === pageSize)

        if (!fetched.length || !nextPageAvailable) {
          hasMore = false
        } else {
          page += 1
        }
      }

      if (!allCertifications.length) {
        toast.error("No certifications found for export")
        return
      }

      setExportData(allCertifications.map(mapCertificationForExport))
      setShowExcelModal(true)
    } catch (error: any) {
      console.error("Error exporting certifications:", error)
      toast.error(error?.response?.data?.message || "Failed to prepare export data")
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Training Certifications</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              {exporting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <FaFileExcel className="w-4 h-4" />
                  Export Excel
                </>
              )}
            </button>
            {canCreateCertification && (
              <button onClick={onAdd} className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors text-sm font-medium shadow-sm">
                <FaPlus className="w-4 h-4" /> Add Certification
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search certificates..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Deduction Status</label>
            <select value={deductionFilter} onChange={(e) => { setDeductionFilter(e.target.value); onDeductionFilter(e.target.value) }} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="deducted">Deducted</option>
              <option value="waived">Waived</option>
            </select>
          </div>
          <div className="w-full sm:w-auto flex items-end">
            <button
              type="button"
              onClick={() => setIsFilterModalOpen(true)}
              className={`inline-flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2 border rounded text-sm font-medium transition-colors ${
                getActiveFiltersCount() > 0 ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Actions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button type="button" onClick={() => handleColumnSort("certificate_number")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Certificate No. {getSortIcon("certificate_number")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Training Request ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">Candidate</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">Department</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px]">Project Group</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">Reporting Person</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Training Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Institute</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">
                <button type="button" onClick={() => handleColumnSort("training_date")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Training Date {getSortIcon("training_date")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">
                <button type="button" onClick={() => handleColumnSort("expiry_date")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Expiry Date {getSortIcon("expiry_date")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button type="button" onClick={() => handleColumnSort("training_amount")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Amount {getSortIcon("training_amount")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button type="button" onClick={() => handleColumnSort("deduction_status")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Deduction {getSortIcon("deduction_status")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">Deduction Remarks</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Association</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">Employee Code</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[80px]">Permanent</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={17} className="px-4 py-12 text-center"><div className="flex flex-col items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div><span className="text-sm text-gray-600">Loading certifications...</span></div></td></tr>
            ) : certifications.length === 0 ? (
              <tr><td colSpan={17} className="px-4 py-12 text-center"><div className="flex flex-col items-center"><div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3"><FaCertificate className="w-6 h-6 text-gray-400" /></div><p className="text-sm text-gray-500">No certifications found</p></div></td></tr>
            ) : (
              certifications.map((cert) => (
                <tr key={cert.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {canEditCertification && (
                        <button onClick={() => onEdit(cert)} className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded" title="Edit"><FaEdit className="w-4 h-4" /></button>
                      )}
                      {cert.training_request_id && (
                        <Link to={`/training/requests/${cert.training_request_id}`} className="text-purple-600 hover:text-purple-800 p-2 hover:bg-purple-50 rounded" title="View Training Request">
                          <FiExternalLink className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cert.certificate_number}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {cert.training_request?.request_id ? (
                      cert.training_request_id ? (
                        <Link to={`/training/requests/${cert.training_request_id}`} className="text-blue-600 hover:text-blue-800 hover:underline">{cert.training_request.request_id}</Link>
                      ) : cert.training_request.request_id
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{cert.training_request?.candidate_name || "-"}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{getDepartmentNames(cert).join(", ") || <span className="text-gray-400">-</span>}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {(() => {
                      const projectNames = getProjectGroupNames(cert)
                      if (projectNames.length === 0) return <span className="text-gray-400">-</span>
                      return (
                        <div className="flex flex-wrap gap-1 items-center max-w-[220px]">
                          {projectNames.slice(0, 2).map((name, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100" title={name}>
                              {name}
                            </span>
                          ))}
                          {projectNames.length > 2 && (
                            <button
                              onClick={() => setProjectsPopup({ certId: cert.id, projects: projectNames })}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-50 text-gray-600 border border-dashed border-gray-300 hover:bg-blue-50 hover:text-blue-700 hover:border-solid hover:border-blue-200 transition-all duration-200"
                              title="View all projects"
                            >
                              +{projectNames.length - 2} more
                            </button>
                          )}
                        </div>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{getReportingPersonNames(cert).join(", ") || <span className="text-gray-400">-</span>}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{cert.training_type?.training_name || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{cert.training_institute?.institute_name || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(cert.training_date)}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`text-sm ${isExpired(cert.expiry_date) ? 'text-red-600 font-medium' : isExpiringSoon(cert.expiry_date) ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                      {formatDate(cert.expiry_date)}
                      {isExpired(cert.expiry_date) && <span className="ml-1 text-xs">(Expired)</span>}
                      {isExpiringSoon(cert.expiry_date) && <span className="ml-1 text-xs">(Expiring)</span>}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatAmount(cert.training_amount)}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{getDeductionBadge(cert.deduction_status, cert.deduction_amount)}</td>
                  <td className="px-4 py-4 text-sm text-gray-600 max-w-[220px]">
                    {cert.deduction_remarks ? <span className="line-clamp-2" title={cert.deduction_remarks}>{cert.deduction_remarks}</span> : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {cert.association_type ? (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cert.association_type === 'onrole' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>
                        {cert.association_type === 'onrole' ? 'On-Role' : 'Off-Role'}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{cert.employee_code || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {cert.linked_to_permanent_user ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Yes</span>
                    ) : <span className="text-xs text-gray-400">No</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationComponent pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        trainingTypeOptions={trainingTypeOptions}
        trainingInstituteOptions={trainingInstituteOptions}
        trainingRequestOptions={trainingRequestOptions}
      />
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="training_certifications"
        title="Export Training Certifications"
        description="Download comprehensive training certification details"
      />

      {/* Projects popup modal */}
      {projectsPopup && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setProjectsPopup(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-50 p-5 min-w-[280px] max-w-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">All Projects</h3>
              <button onClick={() => setProjectsPopup(null)} className="p-1 hover:bg-gray-100 rounded">
                <FaTimesIcon className="w-3.5 h-3.5 text-gray-500" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {projectsPopup.projects.map((name, idx) => (
                <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  {name}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

    </div>
  )
}
